import type { WsReconnectPhase } from "~/rpc/wsConnectionState";

export const CMMD_STARTUP_AUTH_RETRY_DELAY_MS = 1_000;
export const CMMD_STARTUP_AUTH_MAX_ATTEMPTS = 2;

export type StartupReconnectVisibility = "hidden" | "startup-pending" | "actionable";

export type CmmdStartupPhase = "idle" | "auth-refreshing";

export type CmmdStartupAuthFailureDecision =
  | {
      readonly kind: "retry";
      readonly delayMs: number;
      readonly nextAttempt: number;
    }
  | {
      readonly kind: "terminal";
      readonly attempt: number;
    };

export function resolveStartupReconnectVisibility(input: {
  readonly hasConnected: boolean;
  readonly stableConnectionSeen: boolean;
  readonly reconnectPhase: WsReconnectPhase;
}): StartupReconnectVisibility {
  if (input.reconnectPhase === "idle") {
    return "hidden";
  }
  if (input.reconnectPhase === "exhausted") {
    return "actionable";
  }
  if (!input.hasConnected || !input.stableConnectionSeen) {
    return "startup-pending";
  }
  return "actionable";
}

export function resolveCmmdStartupAuthFailureDecision(input: {
  readonly failedAttempt: number;
  readonly maxAttempts?: number;
  readonly retryDelayMs?: number;
}): CmmdStartupAuthFailureDecision {
  const maxAttempts = input.maxAttempts ?? CMMD_STARTUP_AUTH_MAX_ATTEMPTS;
  const retryDelayMs = input.retryDelayMs ?? CMMD_STARTUP_AUTH_RETRY_DELAY_MS;
  if (input.failedAttempt < maxAttempts) {
    return {
      kind: "retry",
      delayMs: retryDelayMs,
      nextAttempt: input.failedAttempt + 1,
    };
  }
  return { kind: "terminal", attempt: input.failedAttempt };
}

export function shouldSurfaceCmmdSessionExpired(input: {
  readonly authExpired: boolean;
  readonly startupPhase: CmmdStartupPhase;
}): boolean {
  return input.authExpired && input.startupPhase !== "auth-refreshing";
}
